import React, { useState, useMemo } from 'react';
import { type Task } from '../../../services/projectManagement/tasksService';
import type { ProjectManagementMember } from '../../../services/projectManagement/membersService';
import { getDisplayName } from '../../../services/projectManagement/membersService';

interface TaskTableViewProps {
  tasks: Task[];
  memberMap: Record<string, ProjectManagementMember>;
  onTaskClick: (taskId: string) => void;
  onTaskUpdate: (taskId: string, updates: Partial<Task>) => Promise<void>;
  sortField?: SortField;
  onSortFieldChange?: (v: SortField) => void;
  sortDirection?: SortDirection;
  onSortDirectionChange?: (v: SortDirection) => void;
  visibleColumns?: TaskTableColumnId[];
  onVisibleColumnsChange?: (cols: TaskTableColumnId[]) => void;
}

export type SortField = 'title' | 'status' | 'priority' | 'due_date' | 'assignee_id' | 'project_name' | 'estimated_time';
export type SortDirection = 'asc' | 'desc';

export type TaskTableColumnId = 'status' | 'priority' | 'assignee_id' | 'project_name' | 'due_date' | 'estimated_time';

const TaskTableView: React.FC<TaskTableViewProps> = ({
  tasks,
  memberMap,
  onTaskClick,
  onTaskUpdate,
  sortField: sortFieldProp,
  onSortFieldChange,
  sortDirection: sortDirectionProp,
  onSortDirectionChange,
  visibleColumns: visibleColumnsProp,
  onVisibleColumnsChange,
}) => {
  const [sortFieldInternal, setSortFieldInternal] = useState<SortField>('due_date');
  const [sortDirectionInternal, setSortDirectionInternal] = useState<SortDirection>('asc');
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());
  const [showColumns, setShowColumns] = useState(false);

  const defaultColumns: TaskTableColumnId[] = ['status', 'priority', 'assignee_id', 'project_name', 'due_date', 'estimated_time'];
  const [visibleColumnsInternal, setVisibleColumnsInternal] = useState<TaskTableColumnId[]>(defaultColumns);

  const sortField = sortFieldProp ?? sortFieldInternal;
  const setSortField = onSortFieldChange ?? setSortFieldInternal;
  const sortDirection = sortDirectionProp ?? sortDirectionInternal;
  const setSortDirection = onSortDirectionChange ?? setSortDirectionInternal;

  const visibleColumns = visibleColumnsProp ?? visibleColumnsInternal;
  const setVisibleColumns = onVisibleColumnsChange ?? setVisibleColumnsInternal;

  const visibleSet = useMemo(() => new Set(visibleColumns), [visibleColumns]);

  const toggleColumn = (col: TaskTableColumnId) => {
    const next = new Set(visibleColumns);
    if (next.has(col)) next.delete(col);
    else next.add(col);
    setVisibleColumns(Array.from(next));
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedTasks = useMemo(() => {
    return [...tasks].sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];

      // Handle null/undefined values
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      // Convert to comparable format
      if (sortField === 'due_date') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [tasks, sortField, sortDirection]);

  const handleSelectAll = () => {
    if (selectedTasks.size === tasks.length) {
      setSelectedTasks(new Set());
    } else {
      setSelectedTasks(new Set(tasks.map(t => t.id)));
    }
  };

  const handleSelectTask = (taskId: string) => {
    const newSelected = new Set(selectedTasks);
    if (newSelected.has(taskId)) {
      newSelected.delete(taskId);
    } else {
      newSelected.add(taskId);
    }
    setSelectedTasks(newSelected);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'done': return 'bg-green-100 text-green-800 border-green-200';
      case 'in-progress': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'review': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'blocked': return 'bg-red-100 text-red-800 border-red-200';
      case 'todo': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) {
      return <span className="text-gray-400">↕</span>;
    }
    return <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  const formatDate = (date: string | null) => {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isOverdue = (task: Task) => {
    if (!task.due_date || task.status === 'done') return false;
    return new Date(task.due_date) < new Date();
  };

  return (
    <div className="space-y-4">
      {/* Bulk Actions */}
      {selectedTasks.size > 0 && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 flex items-center justify-between">
          <span className="text-sm font-medium text-blue-900">
            {selectedTasks.size} task{selectedTasks.size > 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                // Bulk update status
                selectedTasks.forEach(taskId => {
                  void onTaskUpdate(taskId, { status: 'done' });
                });
                setSelectedTasks(new Set());
              }}
              className="rounded-md bg-green-600 px-3 py-1 text-sm font-medium text-white hover:bg-green-700"
            >
              Mark as Done
            </button>
            <button
              onClick={() => setSelectedTasks(new Set())}
              className="rounded-md bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-300"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {tasks.length} task{tasks.length !== 1 ? 's' : ''}
        </div>
        <div className="relative">
          <button
            onClick={() => setShowColumns(!showColumns)}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Columns
          </button>
          {showColumns && (
            <div className="absolute right-0 z-10 mt-2 w-64 rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
              <div className="text-xs font-semibold text-gray-600 mb-2">Visible columns</div>
              <div className="space-y-2">
                {(
                  [
                    { id: 'status', label: 'Status' },
                    { id: 'priority', label: 'Priority' },
                    { id: 'assignee_id', label: 'Assignee' },
                    { id: 'project_name', label: 'Project' },
                    { id: 'due_date', label: 'Due Date' },
                    { id: 'estimated_time', label: 'Time' },
                  ] as Array<{ id: TaskTableColumnId; label: string }>
                ).map(c => (
                  <label key={c.id} className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={visibleSet.has(c.id)}
                      onChange={() => toggleColumn(c.id)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    {c.label}
                  </label>
                ))}
              </div>
              <div className="mt-3 flex justify-end">
                <button
                  onClick={() => setShowColumns(false)}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-12 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedTasks.size === tasks.length && tasks.length > 0}
                    onChange={handleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th
                  onClick={() => handleSort('title')}
                  className="cursor-pointer px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hover:bg-gray-100"
                >
                  <div className="flex items-center gap-2">
                    Task <SortIcon field="title" />
                  </div>
                </th>
                {visibleSet.has('status') && (
                  <th
                    onClick={() => handleSort('status')}
                    className="cursor-pointer px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-2">
                      Status <SortIcon field="status" />
                    </div>
                  </th>
                )}
                {visibleSet.has('priority') && (
                  <th
                    onClick={() => handleSort('priority')}
                    className="cursor-pointer px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-2">
                      Priority <SortIcon field="priority" />
                    </div>
                  </th>
                )}
                {visibleSet.has('assignee_id') && (
                  <th
                    onClick={() => handleSort('assignee_id')}
                    className="cursor-pointer px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-2">
                      Assignee <SortIcon field="assignee_id" />
                    </div>
                  </th>
                )}
                {visibleSet.has('project_name') && (
                  <th
                    onClick={() => handleSort('project_name')}
                    className="cursor-pointer px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-2">
                      Project <SortIcon field="project_name" />
                    </div>
                  </th>
                )}
                {visibleSet.has('due_date') && (
                  <th
                    onClick={() => handleSort('due_date')}
                    className="cursor-pointer px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-2">
                      Due Date <SortIcon field="due_date" />
                    </div>
                  </th>
                )}
                {visibleSet.has('estimated_time') && (
                  <th
                    onClick={() => handleSort('estimated_time')}
                    className="cursor-pointer px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-2">
                      Time <SortIcon field="estimated_time" />
                    </div>
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {sortedTasks.map((task) => (
                <tr
                  key={task.id}
                  className={`hover:bg-gray-50 transition-colors ${
                    isOverdue(task) ? 'bg-red-50' : ''
                  }`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedTasks.has(task.id)}
                      onChange={() => handleSelectTask(task.id)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => onTaskClick(task.id)}
                      className="text-left"
                    >
                      <div className="flex items-center gap-2">
                        {task.status === 'done' && <span className="text-green-600">✓</span>}
                        {isOverdue(task) && <span className="text-red-600">⚠️</span>}
                        <span className="font-medium text-gray-900 hover:text-blue-600">
                          {task.title}
                        </span>
                      </div>
                      {task.description && (
                        <p className="mt-1 text-sm text-gray-500 truncate max-w-md">
                          {task.description}
                        </p>
                      )}
                    </button>
                  </td>
                  {visibleSet.has('status') && (
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${getStatusColor(task.status)}`}>
                        {task.status}
                      </span>
                    </td>
                  )}
                  {visibleSet.has('priority') && (
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                      </span>
                    </td>
                  )}
                  {visibleSet.has('assignee_id') && (
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {task.assignee_id ? getDisplayName(memberMap[task.assignee_id] || null) : '-'}
                    </td>
                  )}
                  {visibleSet.has('project_name') && (
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {task.project_name}
                    </td>
                  )}
                  {visibleSet.has('due_date') && (
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {formatDate(task.due_date || null)}
                    </td>
                  )}
                  {visibleSet.has('estimated_time') && (
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {task.estimated_time ? `${task.estimated_time}h` : '-'}
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <button
                      onClick={() => onTaskClick(task.id)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {sortedTasks.length === 0 && (
            <div className="py-12 text-center">
              <p className="text-gray-500">No tasks to display</p>
            </div>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>
          Showing {sortedTasks.length} task{sortedTasks.length !== 1 ? 's' : ''}
        </span>
        <span>
          {tasks.filter(t => t.status === 'done').length} completed •{' '}
          {tasks.filter(t => isOverdue(t)).length} overdue
        </span>
      </div>
    </div>
  );
};

export default TaskTableView;
